import _ from 'lodash';
import { combineReducers } from 'redux';

import types from 'frontend/state/actionTypes';

import {
  isRingOrTrinket,
  defaultSlot,
  canRollSecondary,
  secondaryBonuses,
  getWeaponHands,
} from 'shared/items';

const idReducer = (state = 0, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.ADD_ITEM:
    case types.ADD_GEARSET:
      return payload.itemId;
    default:
      return state;
  }
};

const itemReducer = (state = null, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      return payload.item;
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return null;
    default:
      return state;
  }
};

const itemSlotReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM_SLOT:
      return payload.slot;
    // if finger or trinket, set default to slot1
    case types.INPUT_ITEM:
      if (isRingOrTrinket(payload.item)) {
        return defaultSlot(payload.item);
      }
      return '';
    // reset on successful submit or item change
    case types.SUBMIT_SIM_SUCCESS:
      return '';
    default:
      return state;
  }
};

const itemLevelReducer = (state = 0, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // prepop level with base level from the item
    case types.INPUT_ITEM:
      if (payload.item) {
        if (payload.item.quality === 5) {
          return 265;
        }
        return payload.item.itemLevel ? payload.item.itemLevel : 0;
      }
      return 0;
    case types.INPUT_ITEM_LEVEL:
      return payload.ilvl ? parseInt(payload.ilvl, 10) : 0;
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return 0;
    default:
      return state;
  }
};

const itemGemReducer = (slot) => (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM_GEM:
      if (slot === payload.slot) {
        return payload.gem;
      }
      return state;
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
    case types.INPUT_ITEM:
      return '';
    default:
      return state;
  }
};

const itemGemLevelReducer = (slot) => (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM_GEM_LEVEL:
      if (slot === payload.slot) {
        return payload.level;
      }
      return state;
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
    case types.INPUT_ITEM:
      return '';
    default:
      return state;
  }
};

const itemEnchantReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM_ENCHANT:
      return payload.enchant;
    // reset on successful submit or item change
    case types.SUBMIT_SIM_SUCCESS:
    case types.INPUT_ITEM:
      return '';
    default:
      return state;
  }
};

const itemSecondaryReducer = (state = 0, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      if (canRollSecondary(payload.item)) {
        return _.first(secondaryBonuses).bonusId;
      }
      return 0;
    case types.INPUT_ITEM_SECONDARY:
      return parseInt(payload.bonusId, 10);
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return 0;
    default:
      return state;
  }
};

const itemAffixReducer = (state = 0, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      return 0;
    case types.INPUT_ITEM_AFFIX:
      return parseInt(payload.bonusId, 10);
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return 0;
    default:
      return state;
  }
};

const handSlotReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM: {
      const weaponsHandiness = getWeaponHands(payload.item);
      if (weaponsHandiness) {
        return weaponsHandiness === 'oh' ? 'off_hand' : 'main_hand';
      }
      return '';
    }
    case types.INPUT_ITEM_HAND:
      return payload.hand;
    case types.SUBMIT_SIM_SUCCESS:
      return '';
    default:
      return state;
  }
};

const itemLegendaryPowerReducer = (state = 0, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      return 0;
    case types.INPUT_ITEM_LEGENDARY_POWER:
      return parseInt(payload.powerId, 10);
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return 0;
    default:
      return state;
  }
};

const itemLegendaryStatsReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      return '';
    case types.INPUT_ITEM_LEGENDARY_STATS:
      return payload.stats;
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return '';
    default:
      return state;
  }
};


const itemCraftedStatsReducer = (state = '', action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      return '';
    case types.INPUT_ITEM_CRAFTED_STATS:
      return payload.stats;
    // reset on successful submit
    case types.SUBMIT_SIM_SUCCESS:
      return '';
    default:
      return state;
  }
};

const itemEmbellishmentReducer = (state = 0, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case types.INPUT_ITEM:
      return 0;
    case types.INPUT_ITEM_EMBELLISHMENT:
      return payload.embellishmentId;
    case types.SUBMIT_SIM_SUCCESS:
      return 0;
    default:
      return state;
  }
};

export default combineReducers({
  id: idReducer,
  item: itemReducer,
  itemSlot: itemSlotReducer,
  itemLevel: itemLevelReducer,
  itemGem0: itemGemReducer(0),
  itemGemLevel0: itemGemLevelReducer(0),
  itemGem1: itemGemReducer(1),
  itemGemLevel1: itemGemLevelReducer(1),
  itemGem2: itemGemReducer(2),
  itemGemLevel2: itemGemLevelReducer(2),
  itemEnchant: itemEnchantReducer,
  itemSecondary: itemSecondaryReducer,
  itemAffix: itemAffixReducer,
  itemHand: handSlotReducer,
  legendaryPower: itemLegendaryPowerReducer,
  legendaryStats: itemLegendaryStatsReducer,
  craftedStats: itemCraftedStatsReducer,
  embellishment: itemEmbellishmentReducer,
});
